import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  TouchAppOutlined as PickIcon,
  AccountBalanceWalletOutlined as BuyIcon,
  CasinoOutlined as VRFIcon,
  EmojiEventsOutlined as PayoutIcon,
  HelpOutline as HelpIcon,
  RocketLaunchOutlined as RocketIcon,
  SportsEsportsOutlined as MineIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";

const RULES_BY_TYPE = {
  0: [
    {
      icon: PickIcon,
      step: "Step 1",
      title: "Pick Your Segment",
      desc: "Choose your lucky number or color segment on the wheel.",
      color: colors.primary.main,
      bg: colors.blue[50],
    },
    {
      icon: BuyIcon,
      step: "Step 2",
      title: "Submit On-Chain",
      desc: "Confirm your spin transaction using your non-custodial Web3 wallet.",
      color: colors.emerald[600],
      bg: colors.emerald[50],
    },
    {
      icon: VRFIcon,
      step: "Step 3",
      title: "Verifiable Spin",
      desc: "Smart contract validates spin outcome using provably fair on-chain randomness.",
      color: colors.purple[600],
      bg: colors.purple[50],
    },
    {
      icon: PayoutIcon,
      step: "Step 4",
      title: "Instant Rewards",
      desc: "Winnings are transferred directly into your wallet automatically.",
      color: colors.amber[600],
      bg: colors.amber[50],
    },
  ],
  1: [
    {
      icon: MineIcon,
      step: "Step 1",
      title: "Pick Unsold Ticket",
      desc: "Select 1 available ticket number from the active minefield grid.",
      color: colors.purple[600],
      bg: colors.purple[50],
    },
    {
      icon: BuyIcon,
      step: "Step 2",
      title: "Secure Your Ticket",
      desc: "Complete purchase on-chain; your ticket is locked exclusively to your wallet address.",
      color: colors.primary.main,
      bg: colors.blue[50],
    },
    {
      icon: VRFIcon,
      step: "Step 3",
      title: "Sell-out Draw Trigger",
      desc: "Once all tickets in the pool are sold, Chainlink VRF automatically triggers the draw.",
      color: colors.emerald[600],
      bg: colors.emerald[50],
    },
    {
      icon: PayoutIcon,
      step: "Step 4",
      title: "Instant Jackpot Payout",
      desc: "The entire prize pool is disbursed automatically to the single winning ticket holder.",
      color: colors.amber[600],
      bg: colors.amber[50],
    },
  ],
  2: [
    {
      icon: PickIcon,
      step: "Step 1",
      title: "Pick Lucky Numbers",
      desc: "Choose your unique combination of numbers from the pool or use Quick Pick.",
      color: colors.primary.main,
      bg: colors.blue[50],
    },
    {
      icon: BuyIcon,
      step: "Step 2",
      title: "Confirm On-Chain",
      desc: "Submit your ticket purchase using your Web3 wallet. Numbers are recorded on-chain.",
      color: colors.emerald[600],
      bg: colors.emerald[50],
    },
    {
      icon: VRFIcon,
      step: "Step 3",
      title: "Provably Fair Draw",
      desc: "At draw time, Chainlink VRF provides tamper-proof, verifiable random winning numbers.",
      color: colors.purple[600],
      bg: colors.purple[50],
    },
    {
      icon: PayoutIcon,
      step: "Step 4",
      title: "Instant Automated Payout",
      desc: "Winning prizes are sent directly from the smart contract to the winner's wallet.",
      color: colors.amber[600],
      bg: colors.amber[50],
    },
  ],
  3: [
    {
      icon: RocketIcon,
      step: "Step 1",
      title: "Set Launch Target",
      desc: "Enter your target digits matching the missile level and tier capacity.",
      color: colors.amber[600],
      bg: colors.amber[50],
    },
    {
      icon: BuyIcon,
      step: "Step 2",
      title: "Confirm Launch",
      desc: "Send entry fee through your wallet to register your trajectory on the blockchain.",
      color: colors.primary.main,
      bg: colors.blue[50],
    },
    {
      icon: VRFIcon,
      step: "Step 3",
      title: "Trajectory Verification",
      desc: "Smart contract executes verified calculation against the cryptographic target.",
      color: colors.purple[600],
      bg: colors.purple[50],
    },
    {
      icon: PayoutIcon,
      step: "Step 4",
      title: "Instant Multiplier Win",
      desc: "Successful strikes receive automated instant payouts directly from the smart contract.",
      color: colors.emerald[600],
      bg: colors.emerald[50],
    },
  ],
};

const LotteryRulesInfo = ({ lotteryType = "2" }) => {
  const typeId = Number(lotteryType) || 2;
  const steps = RULES_BY_TYPE[typeId] || RULES_BY_TYPE[2];

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.lg || "16px",
        border: `1px solid ${colors.slate[200]}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
        bgcolor: "#ffffff",
        mt: 4,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
          <HelpIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.slate[900] }}>
            How It Works
          </Typography>
        </Stack>

        <Grid container spacing={2.5}>
          {steps.map((item, idx) => {
            const IconComp = item.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box
                  sx={{
                    p: 2,
                    height: "100%",
                    borderRadius: borderRadiusPx.md || "12px",
                    bgcolor: colors.slate[50],
                    border: `1px solid ${colors.slate[100]}`,
                    transition: "transform 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: colors.slate[200],
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      bgcolor: item.bg,
                      color: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    <IconComp fontSize="small" />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: item.color,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      display: "block",
                      mb: 0.25,
                    }}
                  >
                    {item.step}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.slate[900], mb: 0.5 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.slate[500], fontSize: "0.825rem", lineHeight: 1.45 }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default LotteryRulesInfo;
